import './OfferFilters.scss';
import { useEffect, useMemo, useRef, useState } from 'react';

/*
  Filter bar for the offer/benefit listing: category dropdown on the left,
  sort dropdown on the right.

  Pluggable, self-contained component: all CSS is scoped under ".of-bar"
  with "of-" prefixed classes, same approach as OfferCard, so it can be
  dropped above any offer grid without colliding with global styles.

  Categories come from the API (the distinct values stored on benefits),
  with "All Categories" prepended as the default. The component is
  controlled - it holds no state beyond which dropdown is open, and
  reports every change to the parent, which owns the selected filters and
  does the fetching.

  Both controls are dropdowns rather than chips/pills so the bar keeps one
  fixed height however many categories come back from the API. See the
  /live-test page (src/components/common/LiveTestStatic/) for the mockup
  this was ported from.
*/

export const OFFER_SORTS = {
  TOP_RATED: 'topRated',
  LATEST: 'latest',
};

const SORT_OPTIONS = [
  { value: OFFER_SORTS.TOP_RATED, label: 'Top Rated' },
  { value: OFFER_SORTS.LATEST, label: 'Latest' },
];

const ALL_CATEGORIES = 'All Categories';

/*
  Listbox-style dropdown. A native <select> cannot be styled to match the
  listing (dark surface, red accent, check on the selected row), so this
  is a button + role="listbox" pair with the keyboard behaviour a select
  is expected to have: arrows/Home/End move the highlight, Enter commits
  it, Escape or a click outside closes without changing the value.
*/
const FilterSelect = ({ id, className = '', label, icon, options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef(null);
  const menuRef = useRef(null);

  const selectedIndex = Math.max(0, options.findIndex((option) => option.value === value));

  const openMenu = () => {
    setActiveIndex(selectedIndex);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  // Keeps the highlighted row visible once the category list outgrows the
  // menu's max-height.
  useEffect(() => {
    if (!open) return;
    menuRef.current?.children[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const commit = (index) => {
    onChange(options[index].value);
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    switch (event.key) {
      case 'Escape':
        setOpen(false);
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (open) setActiveIndex((index) => (index + 1) % options.length);
        else openMenu();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (open) setActiveIndex((index) => (index - 1 + options.length) % options.length);
        else openMenu();
        break;
      case 'Home':
        if (open) {
          event.preventDefault();
          setActiveIndex(0);
        }
        break;
      case 'End':
        if (open) {
          event.preventDefault();
          setActiveIndex(options.length - 1);
        }
        break;
      case 'Enter':
      case ' ':
        // Committing here rather than letting the keypress fall through to
        // the button's click handler, which would just close the menu.
        if (open) {
          event.preventDefault();
          commit(activeIndex);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className={`of-field ${className}`}>
      <span className="of-label" id={`${id}-label`}>{label}</span>

      <div className={`of-select ${open ? 'of-open' : ''}`} ref={rootRef}>
        <button
          type="button"
          className="of-select-btn"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby={`${id}-label`}
          aria-activedescendant={open ? `${id}-option-${activeIndex}` : undefined}
          onClick={() => (open ? setOpen(false) : openMenu())}
          onKeyDown={handleKeyDown}
        >
          <i className={icon} />
          <span className="of-select-value">{options[selectedIndex]?.label}</span>
          <i className="fa-solid fa-chevron-down of-caret" />
        </button>

        <ul className="of-select-menu" role="listbox" aria-labelledby={`${id}-label`} ref={menuRef}>
          {options.map((option, index) => (
            <li
              key={option.value || 'all'}
              id={`${id}-option-${index}`}
              role="option"
              aria-selected={index === selectedIndex}
              className={[
                'of-option',
                index === selectedIndex ? 'of-selected' : '',
                open && index === activeIndex ? 'of-highlight' : '',
              ].join(' ').trim()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => commit(index)}
            >
              <span>{option.label}</span>
              <i className="fa-solid fa-check" />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const OfferFilters = ({
  categories = [],
  activeCategory = '',
  onCategoryChange,
  sort = OFFER_SORTS.TOP_RATED,
  onSortChange,
  resultCount,
  loading = false,
}) => {
  const showCount = !loading && typeof resultCount === 'number';

  const categoryOptions = useMemo(() => ([
    { value: '', label: ALL_CATEGORIES },
    ...categories.map((category) => ({ value: category, label: category })),
  ]), [categories]);

  return (
    <div className="of-bar">
      <div className="of-row">

        <FilterSelect
          id="of-category"
          className="of-field-category"
          label="Category"
          icon="fa-solid fa-layer-group"
          options={categoryOptions}
          value={activeCategory}
          onChange={onCategoryChange}
        />

        <FilterSelect
          id="of-sort"
          className="of-field-sort"
          label="Sort by"
          icon="fa-solid fa-arrow-down-wide-short"
          options={SORT_OPTIONS}
          value={sort}
          onChange={onSortChange}
        />

      </div>

      <div className="of-meta">
        <span className="of-count">
          {loading && 'Loading offers...'}
          {showCount && `${resultCount} ${resultCount === 1 ? 'offer' : 'offers'}`}
          {showCount && activeCategory && <span className="of-count-in"> in {activeCategory}</span>}
        </span>

        {activeCategory && (
          <button type="button" className="of-clear" onClick={() => onCategoryChange('')}>
            <i className="fa-solid fa-xmark" /> Clear filter
          </button>
        )}
      </div>
    </div>
  );
};

export default OfferFilters;
